import { BreadcrumbComponent, Button, ConfirmationPopup, Loader, MyIcon, useToastMessage } from 'ccs-ui-components';
import { debounce } from 'lodash';
import {
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { SideModal } from '../../../../common/components/UI/SideModal/SideModal';
import CommentHistoryModal from '../../../_components/comment-history-modal/CommentHistoryModal';
import { BUSINESS_EVENT, DEBOUNCE_DELAY, EVENT_ASSOCIATION_TABS, EVENT_MODES, EVENT_ROUTE, SIDE_MODAL_BREAKPOINT, SIDE_MODAL_CONSTANTS, STATUS_TYPES, URL_PATH, eventMandateFields } from '../../../constants';
import { useEventForm } from '../../../hooks';
import {
  EventDetailsFormData,
  EventDetailsFormRef,
  IEventValidationHook,
  IEventValidationModel
} from '../../../models/index';
import { AppDispatch, useSelector } from '../../../store/AppStore';
import {
  createEventRequest,
  patchEventRequest,
  updateEventRequest
} from '../../../store/actions/sagaActions/events/CreateEventSagaActions';
import { fetchEventRequest } from '../../../store/actions/sagaActions/events/EventNameSagaActions';
import { getClassNameForStatus } from '../../../utilities/services/ServiceListUtils';
import EventDetailsForm from './event-details-form/EventDetailsForm';
import SchemaDetails from './schema-details-form/SchemaDetails';
import CreateTitleBody from './utilities/CreateTitleBody';
import EventModals from './utilities/EventModals';
import TitleBody from './utilities/TitleBody';

import InfoMessage from '../../../_components/info-messsage/InfoMessage';
import WarningMessage from '../../../_components/warning-message/WarningMessage';
import { fetchEventNameSuccess } from '../../../store/reducer/events/EventNameReducer';
import { clearEvent } from '../../../store/reducer/events/EventReducer';
import { DEPENDENCY_ACTIONS, dependencyGraphInitialState, getDependencyGraphStateReducer } from '../../../utilities/RegistryDependancyManager';
import { checkFormErrors, checkFormValues, checkShouldUpdateEnable, getCreateEventExitPaths, getEventFormFieldsName, getEventFormInputFieldsName, isCreateFlow, resolveEventFormBackPath, shouldShowNavigationAwayPopup } from '../../../utilities/events/EventsListUtils';
import classes from './index.module.scss';

const EventsWrapper = () => {
  const eventNameFieldRef = useRef<EventDetailsFormRef>(null);
  // 3rd party initialization
  const history = useNavigate();
  const route = useLocation();

  const cloneEventName = route.state?.cloneEventName ?? '';
  const registryVersion = route.state?.version ?? '';
  const { t: translate } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { eventname, version, mode } = useParams<{
    eventname: string;
    status: string;
    mode: string;
    version: string;
  }>();
  // states
  const [preview, setPreview] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isCreateClicked, setIsCreateClicked] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [isEditFormClicked, setIsEditFormClicked] = useState(false);
  const [isPatchEventCalled, setIsPatchEventCalled] = useState(false);
  const [isPatchUpdateCalled, setIsPatchUpdateCalled] = useState(false);
  const [isEventUpdateCalled, setIsEventUpdateCalled] = useState(false);
  const [returnedStatusOpen, setReturnedStatusOpen] = useState<boolean>(false);
  const [isCheckClicked, setIsCheckClicked] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [approveModalShow, setApproveModalShow] = useState(false);
  const [editModalShow, setEditModalShow] = useState(false);
  const [sensitiveFieldsArr, setSensitiveFieldsArr] = useState<string[]>([]);
  const [clonedEventDialog, setClonedEventDialog] = useState<boolean>(false);
  const [isEventNameExistWarning, setIsEventNameExistWarning] = useState(false);
  const [isActiveUpdate, setIsActiveUpdate] = useState(false);
  // hooks
  const toastMessage = useToastMessage();
  const [dependencyState, dispatchDependencyState] = useReducer(getDependencyGraphStateReducer, dependencyGraphInitialState);
  const {
    handleInputChange,
    formData,
    setFormParamValues,
  }: IEventValidationHook = useEventForm();
  // get store data
  const {
    errorMessage,
    eventData,
    success,
    isLoading: createEventIsLoading
  } = useSelector((state) => state.eventReducer);
  const { eventDetails, isLoading } = useSelector(
    (state) => state.getEventIdReducer
  );

  /**
   * @method onCreateEventClickHandler
   * @description Handles the click event when creating a new event.
   */
  const onCreateEventClickHandler = () => {
    dispatch(
      createEventRequest({
        eventType: formData.eventType.value,
        eventName: formData.eventName.value,
        eventDescription: formData.description.value,
        eventCategory: BUSINESS_EVENT,
        schema: formData.avroSchema.value,
        dataEncryption: isCheckClicked,
        sensitiveFields: sensitiveFieldsArr,
        schemaName: formData.schemaName.value
      })
    );
  };

  const showEventNameExistWarning = (showServiceExistWarning: boolean) => {
    setIsEventNameExistWarning(showServiceExistWarning);
  };

  /**
   * @method handleReturnButton
   * @description Handles the return button click event.
   * Toggles the state of `returnedStatusOpen`.
   */
  const handleReturnButton = () => {
    setReturnedStatusOpen(!returnedStatusOpen);
  };

  const eventDetailsForHistory = eventDetails[0]?.eventStatusHistory;

  /**
   * @method validateHandleCreateEvent
   * @description Validates the form data and performs necessary actions based on the validation result.
   */
  const validateHandleCreateEvent = () => {
    window.scrollTo(0, 0);
    const requiredFields = getEventFormFieldsName(formData);
    if (requiredFields.some((field) => !formData[field as keyof IEventValidationModel].value)) {
      return false;
    }
    const isFormValid = eventMandateFields.every((name) => formData[name as keyof IEventValidationModel].isValid);
    if (!isFormValid) {
      toastMessage.warning(translate('toast_component.lbl_warning'), translate('events_wrapper.msg_invalid_event_fields_header'));
      return false;
    }
    return true;
  };

  /**
   * @method handleCreateEvent
   * @description Handles the event creation.
   * Sets the `isEventUpdateCalled` state to `false` and calls the `validateHandleCreateEvent` function.
   */
  const handleCreateEvent = () => {
    setIsCreateClicked(true);
    setIsEventUpdateCalled(false);
    if (validateHandleCreateEvent()) {
      onCreateEventClickHandler();
    }
  };

  const callEventDetailsApi = debounce((eventName: string) => {
    setIsEditFormClicked(false);
    setIsPatchEventCalled(false);
    if (cloneEventName?.length) {
      setPreview(false);
      dispatch(fetchEventRequest(cloneEventName, Number(registryVersion)));
    } else {
      setPreview(true);
      dispatch(fetchEventRequest(eventName, Number(version)));
    }
  }, DEBOUNCE_DELAY);

  /**
   * @method handlePatchEvent
   * @description Handles the patch event by dispatching a patchEventRequest action.
   * @param updateStatus - The updated status for the event.
   * @param authComm - The authentication comment for the event.
   */
  const handlePatchEvent = (updateStatus: string, authComm: string) => {
    setIsPatchEventCalled(true);
    dispatch(
      patchEventRequest(
        {
          status: updateStatus,
          comment: authComm
        },
        formData.eventName.value,
        Number(route?.state?.eventDataFromList?.version)
      )
    );
  };

  /**
   * @method handleUpdateEvent
   * @description Handles the update event action.
   * Sets the 'isEventUpdateCalled' state to true and calls the 'validateHandleCreateEvent' function.
   */
  const handleUpdateEvent = () => {
    if (cloneEventName?.length || eventDetails[0]?.status?.toLocaleUpperCase() === STATUS_TYPES.ACTIVE) {
      handleCreateEvent();
      setIsActiveUpdate(true);
      return;
    }
    if (eventDetails[0]?.status?.toLocaleUpperCase() === STATUS_TYPES.INACTIVE) {
      setIsPatchUpdateCalled(true);
      handlePatchEvent(STATUS_TYPES.DRAFT, '');
    } else {
      setIsActiveUpdate(false);
      setIsEventUpdateCalled(true);
      validateHandleCreateEvent();
    }
  };

  /**
   * @method clearSetFormParam
   * @description Clears and sets the form parameters with the provided data.
   * @param formData - The data of the form to be updated.
   */
  const clearSetFormParam = (formData: IEventValidationModel) => {
    const fieldsToReset = getEventFormFieldsName(formData);
    const newData = { ...formData };
    fieldsToReset.forEach((field) => {
      newData[field as keyof IEventValidationModel].value = '';
    });
    setFormParamValues(newData);
  };

  /**
   * @method checkBoxChange
   * @description Handles the change event of the checkbox.
   * @param val - The new value of the checkbox.
   */
  const checkBoxChange = (val: boolean) => {
    setIsCheckClicked(!val);
  };

  const editOrCreateTitle = isEditFormClicked
    ? 'events_wrapper.lbl-edit-event'
    : 'events_wrapper.title_event_wrapper';
  const title = preview
    ? 'events_wrapper.success_title_event_wrapper'
    : editOrCreateTitle;

  /**
   * @method navigateToEditEvent
   * @description Navigates to the edit event page or shows a confirmation dialog based on the event status.
   */
  const navigateToEditEvent = () => {
    if (eventDetails[0].status?.toLocaleUpperCase() === STATUS_TYPES.ACTIVE || eventDetails[0].status?.toLocaleUpperCase() === STATUS_TYPES.INACTIVE) {
      setShowConfirmDialog(true);
    } else {
      handleEditEventForm();
      setIsEditFormClicked(true);
    }
  };

  /**
   * @method handleEditEventForm
   * @description Handles the event when the edit event form is clicked.
   * Sets the returned status open to false, preview to false, and isEditFormClicked to true.
   * Navigates to the edit event page with the appropriate URL and state data.
   */
  const handleEditEventForm = () => {
    setReturnedStatusOpen(false);
    callEventDetailsApi(String(eventname));
    setPreview(false);
    setIsEditFormClicked(true);
    history(
      `${URL_PATH.EVENT_HOME}/${EVENT_MODES.EDIT}/${eventname}/${version}`,
      { state: { ...route.state, eventDataFromList: route.state?.eventDataFromList } }
    );
  };

  /**
  * @method handleEventsCancelBtn
  * @description Handles the click event when the "Cancel" button is clicked to create a new event.
  * Restores the previous URL or navigates to the event home page.
  * Clears the event filter and adds clear data.
  */
  const handleEventsCancelBtn = () => {
    history(resolveEventFormBackPath(route.state));
  };

  /**
   * @method closeCommentDialog
   * @description Closes the comment dialog.
   */
  const closeCommentDialog = () => {
    setReturnedStatusOpen(false);
  };

  const createEventUrlArray = [`${URL_PATH.EVENT_HOME}${URL_PATH.CREATE_EVENT}`];

  const viewEditEventUrlsArray = [`${URL_PATH.EVENT_HOME}${URL_PATH.VIEW_EVENT}/${eventname}/${version}/${EVENT_MODES.EDIT}`];

  /**
   * @method getExceptionalUrlPath
   * @description Returns the URL path based on the event name.
   * If the event name is provided, it returns the view/edit event URLs array.
   * Otherwise, it returns the create event URL array.
   *
   * @returns The URL path based on the event name.
   */
  const getExceptionalUrlPath = () => {
    if (eventname) {
      return viewEditEventUrlsArray;
    }
    return getCreateEventExitPaths(route.state, createEventUrlArray[0]);
  };

  const formInputFields = getEventFormInputFieldsName(formData);
  const formValues = formInputFields.map((field) => formData[field as keyof IEventValidationModel].value);
  const formErrors = formInputFields.map((field) => formData[field as keyof IEventValidationModel].isValid);

  /**
   * @method saveAsDraftEnable
   * @description Determines if the "Save as Draft" feature should be enabled.
   * @returns {boolean} True if the "Save as Draft" feature should be enabled, false otherwise.
   */
  const saveAsDraftEnable = (): boolean => ((checkFormValues(formValues)
    || checkFormErrors(formErrors))
    || checkShouldUpdateEnable(eventDetails, eventData, isEditFormClicked, formData, cloneEventName, sensitiveFieldsArr)
    || (isCheckClicked
      && !sensitiveFieldsArr.length));

  /**
   * @method onConfirmCancelDialogueHandler
   * @description Handles the event when the cancel dialogue is confirmed.
   */
  const onConfirmCancelDialogueHandler = () => {
    history(resolveEventFormBackPath(route.state));
  };

  useEffect(() => () => {
    clearSetFormParam(formData);
  }, []);

  useEffect(() => {
    if (isEventUpdateCalled) {
      dispatch(
        updateEventRequest({
          eventType: formData.eventType.value,
          eventName: formData.eventName.value,
          eventDescription: formData.description.value,
          eventCategory: BUSINESS_EVENT,
          schema: formData.avroSchema.value,
          dataEncryption: isCheckClicked,
          sensitiveFields: sensitiveFieldsArr,
          schemaName: formData.schemaName.value
        }, Number(eventDetails[0]?.version))
      );
    }
  }, [isEventUpdateCalled]);

  // To toggle cloned service dialog pop-up
  const closeClonedEventDialog = () => {
    setClonedEventDialog(false);
  };

  /**
  * If user navigates form the Event List page, then set the preview mode
  */
  useEffect(() => {
    if (cloneEventName) {
      callEventDetailsApi(cloneEventName ?? '');
      setClonedEventDialog(true);
    }
  }, [cloneEventName]);

  /**
   * If user navigates form the Event List page, then set the preview mode
   */
  useEffect(() => {
    if (eventname) {
      callEventDetailsApi(eventname ?? '');
    } else {
      const keys = ['eventName', 'description', 'avroSchema', 'eventType', 'version', 'schemaName'];
      const eventFormData = new EventDetailsFormData();
      keys.forEach((key) => {
        const formKey = key as keyof IEventValidationModel;
        eventFormData[formKey].value = '';
        eventFormData[formKey].isTouched = false;
        eventFormData[formKey].isValid = true;
        eventFormData[formKey].error = '';
      });
      setFormParamValues(eventFormData);
    }
  }, []);

  useEffect(() => {
    const windowScrollStickyHeader = () => {
      const sticky = 10;
      if (window.scrollY > sticky) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };
    window.addEventListener('scroll', windowScrollStickyHeader);

    return (() => {
      window.removeEventListener('scroll', windowScrollStickyHeader);
    });
  }, []);

  useEffect(() => {
    // handle state change in edit and view mode
    if (cloneEventName?.length) {
      setIsEditFormClicked(true);
      setPreview(false);
    }
    if (eventname && mode === EVENT_MODES.EDIT) {
      setIsEditFormClicked(true);
      setPreview(false);
    } else if (eventname && mode === EVENT_MODES.VIEW) {
      setIsEditFormClicked(false);
      setIsPatchEventCalled(false);
      setIsEventUpdateCalled(false);
      setPreview(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (preview || isEditFormClicked) {
      if (eventDetails[0]?.sensitiveFields?.length) {
        setSensitiveFieldsArr(eventDetails[0]?.sensitiveFields);
      }
    }
  }, [eventDetails, isEditFormClicked]);

  useEffect(() => {
    // updates the input field style if error
    if (eventDetails.length && eventDetails[0].eventName && !preview && !isEditFormClicked) {
      const newFormData = { ...formData };
      newFormData.eventName.isValid = false;
      newFormData.eventName.error = String(translate('event_validation_error.txt_existing_name'));

      // Clear all data if input field is empty
      if (!newFormData.eventName.value) {
        newFormData.eventName.error = '';
        newFormData.eventName.isValid = true;
        newFormData.eventName.isTouched = false;
      }
      setFormParamValues(newFormData);
    }
    if (preview || isEditFormClicked) {
      setIsCheckClicked(eventDetails[0]?.dataEncryption ?? false);
    }
  }, [eventDetails, isLoading]);

  useEffect(() => {
    if (success) {
      if (isPatchEventCalled) {
        toastMessage.success(
          translate('toast_component.lbl_success'),
          translate('events_wrapper.msg_patch_event_success_body', {
            replace: { eventName: eventData.eventName }
          })
        );
        setIsPatchEventCalled(false);
        history(
          `${URL_PATH.EVENT_HOME}${URL_PATH.VIEW_EVENT}/${eventData.eventName}/${eventData.version}`,
          { state: { ...route.state, eventDataFromList: eventData } }
        );
        dispatch(fetchEventNameSuccess([eventData]));
      } else if (!isEventUpdateCalled) {
        dispatch(fetchEventNameSuccess([eventData]));
        toastMessage.success(
          translate('toast_component.lbl_success'),
          translate('events_wrapper.msg_create_event_success_body', {
            replace: { eventName: eventData.eventName }
          })
        );
        dispatch(fetchEventNameSuccess([eventData]));
        history(
          `${URL_PATH.EVENT_HOME}${URL_PATH.VIEW_EVENT}/${eventData.eventName}/${eventData.version}`,
          { state: { ...route.state, eventDataFromList: eventData, cloneEventName: '' } }
        );
      } else {
        setIsEditFormClicked(false);
        toastMessage.success(
          translate('toast_component.lbl_success'),
          translate('events_wrapper.msg_patch_event_success_body', {
            replace: { eventName: eventData.eventName }
          })
        );
        if (eventname) {
          dispatch(fetchEventNameSuccess([eventData]));
          history(
            `${URL_PATH.EVENT_HOME}${URL_PATH.VIEW_EVENT}/${eventData.eventName}/${eventData.version}`,
            { state: { ...route.state, eventDataFromList: eventData } }
          );
        }
        setIsEventUpdateCalled(false);
      }
      setPreview(true);
      setIsEventUpdateCalled(false);
    } else if (errorMessage) {
      if (isPatchEventCalled) {
        toastMessage.failure(
          translate('toast_component.lbl_error'),
          translate('events_wrapper.msg_patch_event_fail_body')
        );
      } else if (!isEventUpdateCalled) {
        toastMessage.failure(
          translate('toast_component.lbl_error'),
          translate('events_wrapper.msg_create_event_fail_body')
        );
        dispatch(clearEvent());
      } else {
        toastMessage.failure(
          translate('toast_component.lbl_error'),
          translate('events_wrapper.msg_patch_event_fail_body')
        );
        setIsEventUpdateCalled(false);
      }
    }
    setIsPatchUpdateCalled(false);
  }, [eventData, errorMessage]);

  useEffect(() => {
    const newFormData = { ...formData };
    if (!newFormData.schemaName.value) {
      newFormData.schemaName.error = '';
      newFormData.schemaName.isValid = true;
      newFormData.schemaName.isTouched = false;
      setFormParamValues(newFormData);
    }
  }, [showConfirmDialog]);

  /**
  * Calls on highlight link click
  */
  const onHighlightFieldClick = () => {
    eventNameFieldRef?.current?.onHighlightFieldClick();
  };

  /**
  * Displays the event exist warning
  * @returns ui for warning
  */
  const showEventExistWarning = () => (
    <div className={classes['event-exist-warning']}>
      <div className={classes['warning-pad']} />
      <InfoMessage message="info_message.msg_event_exist" onHighlightFieldClick={onHighlightFieldClick} />
    </div>
  );

  /**
   * Displays the confirmation popup
   */
  const showConfirmationPopup = () =>
  ((shouldShowNavigationAwayPopup(mode, isEventUpdateCalled, isActiveUpdate)
    || (isCreateFlow(route.pathname) && !isCreateClicked))
    && !isPatchUpdateCalled
    ? (
      <ConfirmationPopup urlPathArray={getExceptionalUrlPath()} />
    ) : null);

  const onDependencyGraphFound = (isFound: boolean, registryName: string, entityVersion: string, entityStatus: string) => {
    dispatchDependencyState({ type: DEPENDENCY_ACTIONS.DEPENDENCY_GRAPH_FOUND, addDependencyGraphPayload: { registryName, entityVersion, entityStatus, isDependencyGraphFound: isFound } });
  };

  const onCloseWarningClick = () => {
    dispatchDependencyState({ type: DEPENDENCY_ACTIONS.CLEAR_DEPENDENCY_GRAPH });
  };

  const navigateToEventDependency = () => {
    history(
      `${EVENT_ROUTE.ASSOCIATIONS_ROUTE}${dependencyState.registryName}/${dependencyState.entityVersion}/${EVENT_ASSOCIATION_TABS.SOURCES}`,
      { state: { eventName: dependencyState.registryName, eventVersion: dependencyState.entityVersion, associationStatus: dependencyState.entityStatus } }
    );
  };

  const showDependencyGraphWarning = () => {
    if (dependencyState.isDependencyGraphFound) {
      return (
        <div className={`${classes['event-exist-warning']} ${classes['event-exist-error']}`}>
          <div className={classes['error-pad']} />
          <WarningMessage entityName={dependencyState.registryName} onHighlightFieldClick={navigateToEventDependency} onCloseWarningClick={onCloseWarningClick} />
        </div>
      );
    }
    return null;
  };

  return (
    <Row>
      <Col md={{ offset: 1, span: 10 }}>
        <div className={classes.wrapper}>
          {showConfirmationPopup()}
          <span className={classes.breadcrumb}>
            <BreadcrumbComponent isPreview={preview} fieldName={eventDetails[0]?.eventName} />
          </span>
          <Loader show={createEventIsLoading || isLoading} />
          <EventModals showConfirmDialog={showConfirmDialog} setShowConfirmDialog={setShowConfirmDialog} onConfirmCancelDialogueHandler={onConfirmCancelDialogueHandler} showCommentDialog={showCommentDialog} setShowCommentDialog={setShowCommentDialog} handlePatchEvent={handlePatchEvent} modalShow={modalShow} setModalShow={setModalShow} approveModalShow={approveModalShow} setApproveModalShow={setApproveModalShow} editModalShow={editModalShow} setEditModalShow={setEditModalShow} handleEditEventForm={handleEditEventForm} toastMessage={toastMessage} />

          <div className={`${classes['wrapper-title']} ${isSticky ? classes['sticky-header'] : ''}`}>
            {preview ? <TitleBody isSticky={isSticky} preview={preview} eventDetails={eventDetails} getClassNameForStatus={getClassNameForStatus} handleViewCancelClick={handleEventsCancelBtn} navigateToEditEvent={navigateToEditEvent} setModalShow={setModalShow} handlePatchEvent={handlePatchEvent} setApproveModalShow={setApproveModalShow} setShowCommentDialog={setShowCommentDialog} setEditModalShow={setEditModalShow} mode={mode} eventDetailsForHistory={eventDetailsForHistory} handleReturnButton={handleReturnButton} onDependencyGraphFound={onDependencyGraphFound} />
              : <CreateTitleBody title={title} isSticky={isSticky} preview={preview} isEditFormClicked={isEditFormClicked} isCreateClicked={isCreateClicked} eventDetails={eventDetails} handleCreateEvent={handleCreateEvent} saveAsDraftEnable={saveAsDraftEnable} onCancelCreateEventClicked={handleEventsCancelBtn} handleUpdateEvent={handleUpdateEvent} />}
          </div>
          <Col md={12} className={classes['clone-banner']}>
            {clonedEventDialog && (

              <div className={classes['clone-event-wrapper']}>
                <div className={classes['clone-event-wrapper-content']}>
                  <MyIcon name="infoFillIcon" className={classes['info-icon']} /><span className={classes['font-bold']}>{translate('events_wrapper.clone_event_text')}</span> {translate('serviceWrapper.clone_service_dialog')}
                </div>
                <Button
                  variant="tertiary"
                  className={classes['cancel-icon']}
                  onClick={closeClonedEventDialog}
                  data-testid="cancel-clone-popup"
                  aria-label="cancel-clone-popup"
                >

                  <MyIcon name="xMarkSmallIcon" />
                </Button>
              </div>
            )}
          </Col>

          {isEventNameExistWarning && showEventExistWarning()}
          {showDependencyGraphWarning()}
          <EventDetailsForm
            formData={formData}
            handleInputChange={handleInputChange}
            setFormParamValues={setFormParamValues}
            preview={preview}
            isEditFormClicked={isEditFormClicked}
            checkBoxChange={checkBoxChange}
            isCheckClicked={isCheckClicked}
            setSensitiveFieldsArr={setSensitiveFieldsArr}
            sensitiveFieldsArr={sensitiveFieldsArr}
            showEventNameExistWarning={showEventNameExistWarning}
          />
          <SchemaDetails
            formData={formData}
            handleInputChange={handleInputChange}
            setFormParamValues={setFormParamValues}
            preview={preview}
            isEditFormClicked={isEditFormClicked}
            isCheckClicked={isCheckClicked}
            setSensitiveFieldsArr={setSensitiveFieldsArr}
            sensitiveFieldsArr={sensitiveFieldsArr}
          />
          {
            returnedStatusOpen ? (
              <SideModal
                title={SIDE_MODAL_CONSTANTS.REASON}
                className={classes.sideBarTopAlignment}
                onClose={closeCommentDialog}
                breakPoint={SIDE_MODAL_BREAKPOINT}
                lined
                align="right"
              >
                <CommentHistoryModal eventDetailsForHistory={eventDetailsForHistory} />
              </SideModal>
            ) : (
              ''
            )
          }
        </div>
      </Col>
    </Row>
  );
};

export default EventsWrapper;
